import { Component } from '@angular/core';

@Component({
  selector: 'app-calculator',
  templateUrl: './calculator.component.html',
  styleUrl: './calculator.component.scss',
  standalone: false,
})
export class CalculatorComponent {
  display = '0';
  expression = '';
  isScientific = false;
  private lastResult: number | null = null;

  append(value: string): void {
    if (this.display === '0' && value !== '.' && !'+-*/'.includes(value)) {
      this.display = value;
    } else {
      this.display += value;
    }
    this.expression = this.display;
  }

  clear(): void {
    this.display = '0';
    this.expression = '';
    this.lastResult = null;
  }

  clearEntry(): void {
    this.display = '0';
  }

  backspace(): void {
    if (this.display.length > 1) {
      this.display = this.display.slice(0, -1);
    } else {
      this.display = '0';
    }
    this.expression = this.display;
  }

  appendScientific(func: string): void {
    const needsParen = ['sin', 'cos', 'tan', 'log', 'ln', 'sqrt'].includes(func);
    const token = needsParen ? `${func}(` : func === 'π' ? 'π' : 'e';
    if (this.display === '0' && !needsParen) {
      this.display = token;
    } else if (this.display === '0' && needsParen) {
      this.display = token;
    } else {
      this.display += token;
    }
    this.expression = this.display;
  }

  power(): void {
    this.display += '**';
    this.expression = this.display;
  }

  sqrt(): void {
    const value = this.getDisplayValue();
    if (value === null || value < 0) {
      this.display = value !== null && value < 0 ? 'Error' : '0';
      this.expression = this.display;
      return;
    }
    const result = Math.sqrt(value);
    this.display = this.formatResult(result);
    this.expression = this.display;
  }

  /** Get numeric value from current display (evaluate expression or parse number). */
  private getDisplayValue(): number | null {
    const s = this.display.trim();
    if (!s || s === 'Error') return null;
    try {
      const expr = s
        .replace(/π/g, 'Math.PI')
        .replace(/\be\b/g, 'Math.E')
        .replace(/\bsin\s*\(/g, 'Math.sin(')
        .replace(/\bcos\s*\(/g, 'Math.cos(')
        .replace(/\btan\s*\(/g, 'Math.tan(')
        .replace(/\bln\s*\(/g, 'Math.log(')
        .replace(/\blog\s*\(/g, 'Math.log10(')
        .replace(/\bsqrt\s*\(/g, 'Math.sqrt(')
        .replace(/√\s*\(/g, 'Math.sqrt(')
        .replace(/\^/g, '**');
      if (!/^[\d\s+\-*/().MathPIEsincoqrtgabal_√]+$/.test(expr)) return null;
      const result = Function('"use strict"; return (' + expr + ')')();
      const num = Number(result);
      return Number.isFinite(num) ? num : null;
    } catch {
      const parsed = parseFloat(s);
      return Number.isFinite(parsed) ? parsed : null;
    }
  }
  
  equals(): void {
    try {
      let expr = this.display
        .replace(/π/g, 'Math.PI')
        .replace(/\be\b/g, 'Math.E')
        .replace(/\bsin\s*\(/g, 'Math.sin(')
        .replace(/\bcos\s*\(/g, 'Math.cos(')
        .replace(/\btan\s*\(/g, 'Math.tan(')
        .replace(/\bln\s*\(/g, 'Math.log(')
        .replace(/\blog\s*\(/g, 'Math.log10(')
        .replace(/\bsqrt\s*\(/g, 'Math.sqrt(')
        .replace(/√\s*\(/g, 'Math.sqrt(')
        .replace(/\^/g, '**');
      // Allow only safe characters for evaluation
      if (!/^[\d\s+\-*/().MathPIEsincoqrtgabal_]+$/.test(expr)) {
        this.display = 'Error';
        return;
      }
      const result = Function('"use strict"; return (' + expr + ')')();
      const num = Number(result);
      if (!Number.isFinite(num)) {
        this.display = 'Error';
        return;
      }
      this.lastResult = num;
      this.display = this.formatResult(num);
      this.expression = this.display;
    } catch {
      this.display = 'Error';
    }
  }

  private formatResult(n: number): string {
    const s = String(n);
    if (s.length > 12) {
      return n.toExponential(6);
    }
    return s;
  }

  toggleMode(): void {
    this.isScientific = !this.isScientific;
    if (!this.isScientific) {
      this.display = '0';
      this.expression = '';
    }
  }
}
